import classNames from 'classnames'
import React, { useEffect } from 'react'
import {WebUI, prefix, Warning, getNid} from '../../wui-core/src/index'
import Icon from '../../wui-icon/src'
import type {AlertProps} from './iAlert'

const _PREFIX_CLS = `${prefix}-alert`
const {isShouldUpdate} = Warning;

// 将静态对象移出组件，避免每次render都创建
const ICON_TYPE_MAP: Record<string, string> = {
    info: 'uf-xingzhuangbeifen',
    success: 'uf-chenggongtishi',
    danger: 'uf-exc-t',
    warning: 'uf-exc-t'
};

const TYPE_MAP: Record<string, string> = {
    error: "danger",
    danger: "danger",
    info: "info",
    warning: "warning",
    success: "success",
};

const defaultProps = {
    closeLabel: <Icon type='uf-close' />,
    closable: true,
    showIcon: true,
    dark: false,
    bordered: false
}

// 移除memo以减少批量渲染时的内存开销
// memo会缓存props用于比较，在批量渲染场景下反而增加内存使用
const Alert: React.FC<AlertProps> = (props) => {
    const {
        onDismiss,
        onClose,
        colors = 'warning',
        type,
        className,
        clsPrefix,
        dark = false,
        closable = true,
        showIcon = true,
        icon,
        bordered = false,
        afterClose,
        message,
        description,
        children,
        action,
        id,
        fieldid,
        closeLabel = <Icon type='uf-close' />,
        closeText,
        closeIcon,
        ...others
    } = props;

    // componentWillUnmount等价：只在unmount时调用afterClose
    useEffect(() => {
        // 不在setup中做任何事情
        return () => {
            // 直接使用props中的afterClose，这样确保使用最新值
            afterClose && afterClose();
        };
    }, [afterClose]); // 依赖afterClose，确保引用最新

    // 直接计算样式类，不需要缓存这种轻量计算
    const cls = type && TYPE_MAP[type] ? TYPE_MAP[type] : colors;
    const clsObj = [
        `${clsPrefix}`,
        `${clsPrefix}-${cls}`,
        bordered === true ? `${clsPrefix}-bordered` : '',
        dark ? 'dark' : ''
    ];

    // 直接计算，不需要useMemo包裹
    const adapterNid = getNid(props);

    // 调用应该更新检查
    isShouldUpdate("Alert", props);

    return (
        <div
            {...others}
            role='alert'
            className={classNames(className, clsObj)}
            {...adapterNid}
        >
            {showIcon ? (
                icon ? icon :
                    <Icon
                        className={classNames('show-icon', `${_PREFIX_CLS}-show-icon`)}
                        type={ICON_TYPE_MAP[type || colors]}
                        id={id ? `${id}_alert_${type || colors}` : undefined}
                        fieldid={fieldid ? `${fieldid}_alert_${type || colors}` : undefined}
                    />
            ) : null}

            {/* 内联渲染内容，避免函数调用开销 */}
            <div className={classNames('content', `${_PREFIX_CLS}-content`)}>
                {message ? <div className={classNames('message', `${_PREFIX_CLS}-message`)}>{message}</div> : null}
                {description ? <div className={classNames('description', `${_PREFIX_CLS}-description`)}>{description}</div> : null}
                {!message && !description && children}
            </div>

            {/* 内联渲染操作按钮 */}
            {action ? (
                <div className={classNames(`${_PREFIX_CLS}-action`)}>
                    {action}
                </div>
            ) : null}

            {/* 内联渲染关闭按钮 */}
            {closable && (
                <div
                    className={classNames('close', `${_PREFIX_CLS}-close`)}
                    onClick={onDismiss || onClose}
                    id={id ? `${id}_alert_${colors || type}_close` : undefined}
                    fieldid={fieldid ? `${fieldid}_alert_${colors || type}_close` : undefined}
                >
                    {closeIcon ?? (closeText || closeLabel)}
                </div>
            )}
        </div>
    );
};

// 设置displayName以便调试
Alert.displayName = 'Alert';

export default WebUI({name: 'alert', defaultProps})(Alert);
